import { useEffect, useRef, useState } from "react";
import { ArrowCounterClockwise, Trash, Upload } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { saCard, saLabel } from "../../components/saShared";
import { useBranding } from "../../context/BrandingContext";

const SLOTS = [
  { key: "main", label: "Logo principale", hint: "Sidebar, login, documenti" },
  { key: "compact", label: "Logo compatto", hint: "Header mobile, spazi ridotti" },
  { key: "favicon", label: "Favicon", hint: "Icona del browser (PNG quadrata)" },
  { key: "light", label: "Logo versione chiara", hint: "Per sfondi scuri" },
  { key: "dark", label: "Logo versione scura", hint: "Per sfondi chiari" },
];

const THEME_FIELDS = [
  { key: "primary", label: "Colore primario" },
  { key: "secondary", label: "Colore secondario" },
  { key: "accent", label: "Colore accent" },
  { key: "background", label: "Colore sfondo" },
  { key: "text", label: "Colore testo" },
  { key: "border", label: "Colore bordi" },
];

const FONTS = ["IBM Plex Sans", "Manrope", "Inter", "Montserrat", "Lora", "Playfair Display", "Source Sans 3"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SABranding() {
  const { branding, refresh } = useBranding();
  const [theme, setTheme] = useState(null);
  const [font, setFont] = useState("IBM Plex Sans");
  const [saving, setSaving] = useState(false);
  const fileRefs = useRef({});

  useEffect(() => {
    if (branding) {
      setTheme(branding.theme);
      setFont(branding.font_main || "IBM Plex Sans");
    }
  }, [branding]);

  const upload = async (slot, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/superadmin/branding/assets/${slot}`, { data: reader.result, content_type: file.type });
        toast.success("Logo caricato");
        refresh();
      } catch (e) { toast.error(apiError(e)); }
    };
    reader.readAsDataURL(file);
  };

  const remove = async (slot) => {
    try {
      await api.delete(`/superadmin/branding/assets/${slot}`);
      toast.success("Logo rimosso");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveTheme = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/superadmin/branding", { theme, font_main: font });
      toast.success("Tema salvato e applicato ovunque");
      refresh();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const resetTheme = async () => {
    try {
      await api.post("/superadmin/branding/reset");
      toast.success("Tema predefinito Dishenda ripristinato");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!theme) return <div className="text-sm text-zinc-500 py-10">Caricamento...</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="sa-branding">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">Branding</h1>
        <p className="text-xs text-zinc-500">Logo e tema grafico centralizzati: ogni modifica si applica automaticamente a login, sidebar, dashboard, campagne email e documenti.</p>
      </div>

      <section>
        <h2 className="font-display font-semibold text-white mb-3">Loghi</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {SLOTS.map((s) => {
            const url = branding?.logos?.[s.key];
            return (
              <div key={s.key} className={`${saCard} p-4`} data-testid={`slot-${s.key}`}>
                <div className="text-sm font-semibold text-zinc-100">{s.label}</div>
                <div className="text-[11px] text-zinc-500 mb-3">{s.hint}</div>
                <div className="h-20 rounded-md bg-zinc-800 border border-dashed border-zinc-700 flex items-center justify-center overflow-hidden mb-3">
                  {url ? (
                    <img src={`${BACKEND}${url}?t=${Date.now()}`} alt={s.label} className="max-h-16 max-w-full object-contain" data-testid={`preview-${s.key}`} />
                  ) : (
                    <span className="text-[11px] text-zinc-600">Nessun logo</span>
                  )}
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => fileRefs.current[s.key]?.click()} data-testid={`upload-${s.key}`}
                    className="flex-1 h-9 rounded-md bg-amber-400 text-zinc-950 text-xs font-bold flex items-center justify-center gap-1.5">
                    <Upload size={13} weight="bold" /> {url ? "Sostituisci" : "Carica"}
                  </button>
                  {url && (
                    <button onClick={() => remove(s.key)} data-testid={`remove-${s.key}`}
                      className="h-9 px-3 rounded-md bg-zinc-800 border border-rose-500/40 text-rose-300 text-xs font-bold">
                      <Trash size={13} />
                    </button>
                  )}
                </div>
                <input ref={(el) => (fileRefs.current[s.key] = el)} type="file" accept="image/*" className="hidden"
                  data-testid={`file-${s.key}`} onChange={(e) => upload(s.key, e.target.files?.[0])} />
              </div>
            );
          })}
        </div>
      </section>

      <form onSubmit={saveTheme} className={`${saCard} p-5 space-y-4`}>
        <div className="flex items-center justify-between">
          <h2 className="font-display font-semibold text-white">Tema grafico</h2>
          <button type="button" onClick={resetTheme} data-testid="theme-reset"
            className="h-9 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs font-bold flex items-center gap-1.5">
            <ArrowCounterClockwise size={13} /> Ripristina predefinito
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {THEME_FIELDS.map((f) => (
            <div key={f.key}>
              <label className={saLabel}>{f.label}</label>
              <div className="flex items-center gap-2">
                <input type="color" value={theme[f.key]} onChange={(e) => setTheme({ ...theme, [f.key]: e.target.value })}
                  className="w-10 h-10 rounded-md border border-zinc-700 bg-zinc-800 cursor-pointer" data-testid={`color-${f.key}`} />
                <input value={theme[f.key]} onChange={(e) => setTheme({ ...theme, [f.key]: e.target.value })}
                  className="flex-1 h-10 px-2 rounded-md border border-zinc-700 bg-zinc-800 text-xs text-zinc-100 font-mono" data-testid={`colorhex-${f.key}`} />
              </div>
            </div>
          ))}
        </div>
        <div>
          <label className={saLabel}>Font principale</label>
          <select value={font} onChange={(e) => setFont(e.target.value)}
            className="w-full sm:w-64 h-11 px-3 rounded-md border border-zinc-700 bg-zinc-800 text-sm text-zinc-100" data-testid="font-select">
            {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div className="rounded-lg border border-zinc-700 overflow-hidden" data-testid="theme-preview">
          <div className="p-4" style={{ backgroundColor: theme.background }}>
            <div className="font-bold text-lg" style={{ color: theme.text, fontFamily: font }}>Anteprima tema</div>
            <div className="text-sm" style={{ color: theme.secondary, fontFamily: font }}>Testo secondario e descrizioni</div>
            <div className="flex gap-2 mt-3">
              <span className="px-4 py-2 rounded-md text-sm font-bold" style={{ backgroundColor: theme.primary, color: "#052e12" }}>Pulsante primario</span>
              <span className="px-4 py-2 rounded-md text-sm font-bold" style={{ backgroundColor: theme.accent, color: theme.text }}>Accent</span>
              <span className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: theme.border, color: theme.text }}>Bordi</span>
            </div>
          </div>
        </div>
        <button disabled={saving} className="h-11 px-6 rounded-md bg-amber-400 text-zinc-950 font-bold text-sm disabled:opacity-50" data-testid="theme-save">
          {saving ? "Salvataggio..." : "Salva tema"}
        </button>
      </form>
    </div>
  );
}
